/**
 * Página: Restablecer contraseña (pública, sin login)
 * Se llega acá desde el link del mail de recuperación. Valida el token al
 * cargar (GET, no modifica nada) y si es válido deja elegir una contraseña
 * nueva (con confirmación) — mismo patrón de 2 pasos que ActivarCuenta.jsx,
 * misma identidad visual. Al confirmar, redirige a /login.
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import {
  STROKE_DEGRADE,
  IconContrasena,
  IconMostrarContrasena,
  IconOcultarContrasena,
} from '../components/icons';
import '../styles/login.css';

const API_URL = process.env.REACT_APP_API_URL;

// Cuánto queda a la vista el mensaje "¡Listo!" antes de mandar a /login,
// para que la persona alcance a leerlo en vez de sentir un salto brusco.
const DEMORA_REDIRECCION_MS = 2000;

const RestablecerContrasena = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  // 'validando' | 'valido' | 'invalido' | 'restablecida'
  const [estado, setEstado] = useState('validando');
  const [datosUsuario, setDatosUsuario] = useState(null);
  const [errorValidacion, setErrorValidacion] = useState('');

  const [contrasena, setContrasena] = useState('');
  const [confirmarContrasena, setConfirmarContrasena] = useState('');
  const [mostrarContrasena, setMostrarContrasena] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [errorFormulario, setErrorFormulario] = useState('');

  useEffect(() => {
    const validar = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/auth/restablecer-contrasena/${token}`);
        setDatosUsuario(data);
        setEstado('valido');
      } catch (err) {
        console.error('Error al validar el link de recuperación:', err);
        setErrorValidacion(err.response?.data?.mensaje || 'El link de recuperación no es válido');
        setEstado('invalido');
      }
    };
    validar();
  }, [token]);

  // Redirección automática a /login tras confirmar — con un margen para que
  // se alcance a leer el mensaje de éxito, no un salto instantáneo.
  useEffect(() => {
    if (estado !== 'restablecida') return undefined;
    const temporizador = setTimeout(() => navigate('/login', { replace: true }), DEMORA_REDIRECCION_MS);
    return () => clearTimeout(temporizador);
  }, [estado, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorFormulario('');

    if (contrasena.length < 8) {
      setErrorFormulario('La contraseña debe tener al menos 8 caracteres');
      return;
    }
    if (contrasena !== confirmarContrasena) {
      setErrorFormulario('Las contraseñas no coinciden');
      return;
    }

    try {
      setEnviando(true);
      await axios.post(`${API_URL}/auth/restablecer-contrasena/${token}`, { contrasena });
      setEstado('restablecida');
    } catch (err) {
      console.error('Error al restablecer la contraseña:', err);
      setErrorFormulario(err.response?.data?.mensaje || 'Error al restablecer la contraseña');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-gradient"></div>

      <div className="login-card">
        <div className="login-logo">
          <img src="/logo-betech.svg" alt="Betech" className="logo-img" />
        </div>

        {estado === 'validando' && (
          <>
            <h1 className="login-title">Restablecer contraseña</h1>
            <p className="login-subtitle">Un momento, estamos validando tu link...</p>
          </>
        )}

        {estado === 'invalido' && (
          <>
            <h1 className="login-title">Restablecer contraseña</h1>
            <div className="error-message">{errorValidacion}</div>
            <p className="login-subtitle" style={{ marginTop: 16 }}>
              Pedí un link nuevo o iniciá sesión si ya recordás tu contraseña.
            </p>
            <div className="login-links" style={{ marginTop: 12 }}>
              <Link to="/recuperar-contraseña" className="link-ayuda">Pedir un link nuevo</Link>
              <span className="separador">•</span>
              <Link to="/login" className="link-ayuda">Ir a iniciar sesión</Link>
            </div>
          </>
        )}

        {estado === 'valido' && (
          <>
            <h1 className="login-title">Restablecer contraseña</h1>
            <p className="login-subtitle">
              Hola {datosUsuario.nombre_completo}, elegí tu nueva contraseña para {datosUsuario.email}.
            </p>

            <form onSubmit={handleSubmit} className="login-form">
              <div className="form-group">
                <label htmlFor="contrasena">Contraseña nueva</label>
                <div className="input-wrapper">
                  <span className="input-icon"><IconContrasena size={16} stroke={STROKE_DEGRADE} /></span>
                  <input
                    id="contrasena"
                    type={mostrarContrasena ? 'text' : 'password'}
                    placeholder="Al menos 8 caracteres"
                    value={contrasena}
                    onChange={(e) => setContrasena(e.target.value)}
                    className="form-input"
                    disabled={enviando}
                    required
                  />
                  <button
                    type="button"
                    className="btn-mostrar-contraseña"
                    onClick={() => setMostrarContrasena(!mostrarContrasena)}
                    tabIndex="-1"
                  >
                    {mostrarContrasena
                      ? <IconOcultarContrasena size={16} stroke="#555" />
                      : <IconMostrarContrasena size={16} stroke="#555" />}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="confirmar-contrasena">Confirmar contraseña</label>
                <div className="input-wrapper">
                  <span className="input-icon"><IconContrasena size={16} stroke={STROKE_DEGRADE} /></span>
                  <input
                    id="confirmar-contrasena"
                    type={mostrarContrasena ? 'text' : 'password'}
                    placeholder="Repetí la contraseña"
                    value={confirmarContrasena}
                    onChange={(e) => setConfirmarContrasena(e.target.value)}
                    className="form-input"
                    disabled={enviando}
                    required
                  />
                </div>
              </div>

              {errorFormulario && <div className="error-message">{errorFormulario}</div>}

              <button type="submit" className="btn-ingresar" disabled={enviando}>
                {enviando ? 'Restableciendo...' : 'Restablecer contraseña'}
              </button>
            </form>
          </>
        )}

        {estado === 'restablecida' && (
          <>
            <h1 className="login-title">¡Listo!</h1>
            <p className="login-subtitle">Tu contraseña quedó actualizada. Te llevamos a iniciar sesión...</p>
            <div className="login-links" style={{ marginTop: 16 }}>
              <Link to="/login" className="link-ayuda">Ir a iniciar sesión ahora</Link>
            </div>
          </>
        )}

        <div className="login-footer">
          <p>
            © 2026 Betech Solutions |
            <a href="https://be-tech.com.ar" target="_blank" rel="noopener noreferrer">
              {' '}www.be-tech.com.ar
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RestablecerContrasena;
